import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  SecurityContext,
  ViewChild,
  DOCUMENT
} from '@angular/core';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import {
  Breakpoint,
  BreakpointState,
  ConfigurationService,
  IfFeatureService,
  Level,
  LinkRegistryService,
  Logger,
  MediaQueryService,
  RouterBaseService
} from '@tdwb/core';
import { IntegrationService } from './integration.service';

export interface IntegrationPageConfig {
  linkId: string;
  height: number;
  width: number | string;
  fixed: boolean;
  title?: string;
  url?: string;
  pageHeader?: string;
  backgroundColor?: boolean;
  baseDomainConfigKey?: string; // Specifies configuration call property to get domain
  baseDomain?: string; // The actual allowed domain value
  featureFlagForFullWidth?: string; // featureFlags for iframe with 100% width
}

// eslint-disable-next-line td-wb-rules/change-detection-strategy-rule
@Component({
  selector: 'td-wb-integration',
  templateUrl: 'integration.component.html',
  styleUrls: ['integration.component.scss'],
  standalone: false
})
export class IntegrationComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly LOGGER: Logger = Logger.getLogger('IntegrationComponent').setLevel(Level.ERROR);
  private static readonly INTEGRATION_PAGE_MAP: Map<string, IntegrationPageConfig> = new Map([
    ['page.new-issues', {
      linkId: 'link_new_issues_legacy',
      height: 800,
      width: 962,
      fixed: true,
      pageHeader: 'newIssues',
      featureFlagForFullWidth: 'nipAuthenticationSSO'
    }],
    ['page.fixed-income', {
      linkId: 'link_fixed_income_b2b',
      height: 800,
      width: 962,
      fixed: true,
      pageHeader: 'fixedIncome'
    }],
    ['page.research', {
      linkId: 'link_b2b_transfer',
      height: 2525,
      width: undefined,
      fixed: false,
      backgroundColor: true,
      baseDomainConfigKey: 'modBaseDomain'
    }]
  ]);

  @ViewChild('iframe') iframe: ElementRef;

  config: IntegrationPageConfig;
  iframeSource: SafeResourceUrl;
  contentReady: boolean = false;
  errorSate: boolean = false;
  iframeWidth: number | string;

  private loadInterval: ReturnType<typeof setInterval>;
  private resizeSubscription: Subscription;
  private afterViewInit: boolean = false;
  private mediaQuerySubscription: Subscription;

  constructor(@Inject('window') private browserWindow: Window,
              private renderer: Renderer2,
              @Inject(DOCUMENT) private document: Document,
              private linkRegistryService: LinkRegistryService,
              private routerService: RouterBaseService,
              private sanitizer: DomSanitizer,
              private mediaQueryService: MediaQueryService,
              private configurationService: ConfigurationService,
              private integrationService: IntegrationService,
              private ifFeatureService: IfFeatureService) {
  }

  /// Listener for messages sent from 3rd party clients embedded on iFrame via postMessage
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    const message = event.data;
    if (message?.source === 'NIP') {
      if (message.type === 'KEEPALIVE') {
        this.integrationService.sendKeepAlive();
      }
    } else {
      if (event?.origin !== this.config.baseDomain) {
        IntegrationComponent.LOGGER.error('IntegrationComponent: Message source is empty or not from known domain');
        return;
      }

      if (message?.source === 'MOD') {
        const data: string = message?.data;
        IntegrationComponent.LOGGER.info('IntegrationComponent: MOD message data is ', data);

        switch (message.type) {
          case 'OPEN':
            if (data.match(/\/?#\//)) {
              this.browserWindow.location.replace(data);
            }
            return;
          case 'RESIZE':
            if (data.match(/^\d+(\.\d+)?(px|em|rem|vh|vw|%)$/)) {
              this.renderer.setStyle(this.iframe.nativeElement, 'height', data);
            }
            return;
          case 'SCROLLTOP':
            this.browserWindow.scrollTo(0, 0);
            return;
        }
      }
    }
  }

  ngOnInit(): void {
    this.config = this.getPageConfig();
    this.iframeWidth = this.config.width ?? '100%';
    const iframeSource = this.sanitizer.sanitize(SecurityContext.URL, this.config.url);
    this.iframeSource = this.sanitizer.bypassSecurityTrustResourceUrl(iframeSource);
    if (this.config.backgroundColor) {
      this.addMODStyle();
    }
    if (this.config.width === undefined) {
      this.mediaQuerySubscription = this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD)
        .subscribe((state: BreakpointState): void => {
          this.iframeWidth = state.matches ? 1024 : '100%';
        });
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInit = true;
  }

  ngOnDestroy(): void {
    if (this.loadInterval !== undefined) {
      clearInterval(this.loadInterval);
    }
    this.resizeSubscription?.unsubscribe();
    if (this.config.backgroundColor) {
      this.removeMODStyle();
    }
    this.mediaQuerySubscription?.unsubscribe();
  }

  onIframeLoad(): void {
    if (this.afterViewInit) {
      if (this.config.fixed) {
        this.contentReady = true;
      }
    }
  }

  private getPageConfig(): IntegrationPageConfig {
    const state = this.routerService.getCurrentStateName();
    const stateParams = this.routerService.getStateParams();
    const pageConfiguration = { ...IntegrationComponent.INTEGRATION_PAGE_MAP.get(state) };
    pageConfiguration.title = state;
    pageConfiguration.url = this.linkRegistryService.getLink(pageConfiguration.linkId, stateParams);
    if (pageConfiguration.baseDomainConfigKey !== undefined) {
      const baseDomain: string = this.configurationService.getProperty(pageConfiguration.baseDomainConfigKey, '');
      pageConfiguration.baseDomain = baseDomain.endsWith('/') ? baseDomain.slice(0, -1) : baseDomain;
    }
    if (
      pageConfiguration.featureFlagForFullWidth !== undefined &&
      this.ifFeatureService.getFeatureFlag(pageConfiguration.featureFlagForFullWidth) === true
    ) {
      pageConfiguration.width = '100%';
    }
    return pageConfiguration;
  }

  private addMODStyle(): void {
    this.renderer.addClass(this.document.body, 'td-wb-main--mod');
  }

  private removeMODStyle(): void {
    this.renderer.removeClass(this.document.body, 'td-wb-main--mod');
  }
}
